import { formatDate, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, ViewChild, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatLabel, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { BrandingService } from '@next/core-lib/branding';
import { ChartComponent } from '@next/core-lib/chart';
import {
  SummaryCardAction,
  PrintPageComponent,
  SummaryCardComponent,
  ExtendedNotificationComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe, AgrDatePipe } from '@next/core-lib/i18n';
import { DataSerieItem, DataSeries } from '@next/core-lib/models';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SiteSelectors, SiteState } from '@next/core-lib/site';
import { Customer, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import * as Highcharts from 'highcharts';
import { SeriesOptionsType } from 'highcharts';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import {
  DayPoint,
  IntervalType,
  ObservationsRegistration,
  WithinCyclePerformanceKpi,
  WithinCyclePerformanceKpis,
  WithinCyclePerformanceKpisEnum,
} from './models/within-cycle-performance.model';
import { BrandingLogoSrc } from '../branding/branding.model';
import { CycleOverviewDetailedPanelComponent } from '../cycle/components/cycle-overview-detailed-panel/cycle-overview-detailed-panel.component';
import { WDORegistrationComponent } from '../cycle/components/wdo-registration/wdo-registration.component';
import { WDORegistrationOverviewComponent } from '../cycle/components/wdo-registration-overview/wdo-registration-overview.component';
import { Cycle, Barn, DayOldChick } from '../cycle/models/cycle.model';
import { CycleService } from '../cycle/services/cycle.service';
import * as cycleActions from '../cycle/store/actions/cycle.actions';
import { getCycles, getSelectedCycleId } from '../cycle/store/selectors/cycle.selectors';

import { FeedAdvice } from '../feed-advice/models/feed-advice.models';
import { getFeedAdvices } from '../feed-advice/store/feed-advice.selectors';
import { ReferenceValueSheet } from '../reference-value-sheet/models/reference-value-sheet.model';
import { getReferenceValueSheets } from '../reference-value-sheet/store/reference-value-sheet.selectors';

import { ImportComponent } from './components/import/import.component';
import { WeeklyReportPrintComponent } from './components/weekly-report-print/weekly-report-print.component';
import * as actions from './store/actions/within-cycle-performance.actions';
import {
  getGrowthPredictionDataSeries,
  getObservations,
  getRefValueSheetDataSeries,
  getWdoDataSeries,
} from './store/selectors/within-cycle-performance.selectors';

import { UnitOfMeasureSettings } from '../settings/models/setting.model';
import {
  getDayOffset,
  getObservationSwitchSetting,
  getUnitOfMeasureSettings,
} from '../settings/store/selectors/setting.selector';

@Component({
  selector: 'app-within-cycle-performance',
  templateUrl: './within-cycle-performance.component.html',
  styleUrls: ['./within-cycle-performance.component.scss'],
  imports: [
    MatToolbar,
    MatToolbarRow,
    NgIf,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    MatButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    PrintPageComponent,
    WeeklyReportPrintComponent,
    SummaryCardComponent,
    RouterLink,
    CycleOverviewDetailedPanelComponent,
    ExtendedNotificationComponent,
    MatFormField,
    MatSlideToggle,
    ChartComponent,
    NotificationEmptyComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
  ],
})
export class WithinCyclePerformanceComponent implements OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly router = inject(Router);
  private readonly locale = inject(LocaleService);
  private dialog = inject(DialogService);
  private cycleService = inject(CycleService);
  private readonly brandingService = inject(BrandingService);

  private readonly destroyed$ = new Subject<void>();
  pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  isLineChartVisible = false;
  isColumnChartVisible = false;

  nonCumulativeKpis = WithinCyclePerformanceKpis.filter((k) => k.showInKpiSelector === true);
  selectedKpi$: BehaviorSubject<WithinCyclePerformanceKpi> = new BehaviorSubject(
    this.nonCumulativeKpis[0],
  );
  isCumulativeKpisSelected$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  shouldShowObservationsPanel$ = this.store.select(getObservationSwitchSetting).pipe();

  customer$: Observable<Customer> = this.store.select(UserSelectors.selectCustomer);
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  observations$: Observable<ObservationsRegistration[]> = this.store.select(getObservations);

  isEmptyLineSeries = false;
  isEmptyColumnSeries = false;

  csvDelimiter = ',';
  csvDateFormat = 'MM-dd-yyyy';

  showCycleStartInFutureWarning = false;
  showAgrifirmLogo = true;
  customizedLogoSrc: string = BrandingLogoSrc.PoultryNextLogo;

  selectOptions = [
    {
      value: IntervalType.Day,
      name: this.locale.translate('wdo-registration-overview.daily-registrations'),
    },
    {
      value: IntervalType.Week,
      name: this.locale.translate('wdo-registration-overview.weekly-registrations'),
    },
  ];

  selectedIntervalTypeOption: IntervalType;

  @ViewChild('lineChart') lineChartComponent: ChartComponent;
  @ViewChild('columnChart') columnChartComponent: ChartComponent;
  @ViewChild(WeeklyReportPrintComponent) weeklyReportPrintComponent: WeeklyReportPrintComponent;

  sortedCycles: Cycle[] = [];
  sortedCycles$: Observable<Cycle[]> = this.store.select(getCycles).pipe(
    filter((cycles) => cycles !== undefined && cycles.length > 0),
    takeUntil(this.destroyed$),
    map((cycles) =>
      cycles
        .filter((c) => c.dayOldChicks?.length > 0)
        .sort((a, b) => (a.cycleNumber < b.cycleNumber ? 1 : -1))
        .map(
          (c) =>
            <Cycle>{
              ...c,
              // Sort arrivals.
              dayOldChicks: c.dayOldChicks
                ?.slice()
                .sort((a, b) => (new Date(b.arrivalDate) < new Date(a.arrivalDate) ? 1 : -1)),
            },
        ),
    ),
  );

  selectedCycle$: Observable<Cycle> = combineLatest([
    this.sortedCycles$,
    this.store.select(getSelectedCycleId),
  ]).pipe(
    filter(([cycles]) => cycles !== undefined && cycles.length > 0),
    takeUntil(this.destroyed$),
    map(([cycles, selectedCycleId]) => {
      if (selectedCycleId !== undefined && selectedCycleId > 0) {
        return cycles.find((c) => c.id === selectedCycleId);
      } else {
        return cycles[0];
      }
    }),
  );

  feedAdvices$: Observable<FeedAdvice[]> = this.store.select(getFeedAdvices);
  referenceValueSheets$: Observable<ReferenceValueSheet[]> =
    this.store.select(getReferenceValueSheets);
  refValSheetDataSeries$: Observable<DataSeries<number, number>> = this.store.select(
    getRefValueSheetDataSeries,
  );
  wdoDataSeries$: Observable<DataSeries<number, number>[]> = this.store.select(getWdoDataSeries);
  growthPredictionDataSeries$: Observable<DataSeries<number, number>[]> = this.store.select(
    getGrowthPredictionDataSeries,
  );
  dayOffset$: Observable<number> = this.store.select(getDayOffset);
  unitOfMeasureSettings$: Observable<UnitOfMeasureSettings> =
    this.store.select(getUnitOfMeasureSettings);
  unitOfMeasureSettings: UnitOfMeasureSettings = undefined;
  lineChartDataSeries: SeriesOptionsType[] = [
    {
      id: 'rvs',
      type: 'line',
      dashStyle: 'ShortDash',
      name: `${this.locale.translate('reference-value-sheet.create.title')}`,
      data: [],
    },
  ];
  columnChartDataSeries: SeriesOptionsType[] = [];
  lineTimeSeriesOptions: Highcharts.Options = {};
  columnTimeSeriesOptions: Highcharts.Options = {};
  chartDataSeriesSubscription: Subscription = undefined;
  chartDataSeries$: Observable<void> = combineLatest([
    this.refValSheetDataSeries$,
    this.wdoDataSeries$,
    this.growthPredictionDataSeries$,
    this.selectedKpi$,
    this.isCumulativeKpisSelected$,
    this.dayOffset$,
  ]).pipe(
    map(
      ([refValSheetSeries, wdoSeries, growthPredictionDataSeries, kpi, isCumulative, dayOffset]: [
        DataSeries<number, number>,
        DataSeries<number, number>[],
        DataSeries<number, number>[],
        WithinCyclePerformanceKpi,
        boolean,
        number,
      ]) => {
        const getBarnName = this.getBarnName.bind(this);

        let showChart = false;
        dayOffset = Number(dayOffset);

        let title = `${this.locale.translate(
          `within-cycle-performance.kpis.${kpi.kpi.toString()}`,
        )}${isCumulative ? ` (${this.locale.translate('farmer-insights.aggregation.Sum')})` : ''}`;
        if (kpi.uom) {
          title += ` (${kpi.uom})`;
        }

        if (kpi.kpi !== WithinCyclePerformanceKpisEnum.LITTER_QUALITY) {
          this.lineTimeSeriesOptions = {
            chart: {
              height: '500px',
              type: 'line',
              zooming: {
                type: 'xy',
              },
              panning: {
                enabled: true,
                type: 'xy',
              },
              panKey: 'shift',
            },
            title: {
              text: title,
            },
            credits: {
              enabled: false,
            },
            xAxis: {
              tickInterval: 1,
              title: {
                text: this.locale.translate('days'),
              },
            },
            yAxis: {
              title: {
                text: '',
              },
              labels: {
                formatter() {
                  return `${this.value} ${kpi.uom}`;
                },
              },
            },
          };

          this.lineChartDataSeries = [...this.lineChartDataSeries.filter((s) => s.id === 'rvs')];
          wdoSeries?.forEach((barnSeries) => {
            // Create one or more series for the given KPI
            const wdoSeriesOptions: SeriesOptionsType[] = this.createWdoSeriesOptionsForKpi(
              barnSeries,
              kpi,
              isCumulative,
              dayOffset,
            );

            showChart = wdoSeriesOptions && wdoSeriesOptions.find((w) => w.visible) !== undefined;

            this.lineChartDataSeries = [
              ...this.lineChartDataSeries.filter((s) => s.id !== barnSeries.name),
              ...wdoSeriesOptions,
            ];

            this.lineTimeSeriesOptions.series = this.lineChartDataSeries;
          });

          growthPredictionDataSeries?.forEach((growthPredictionDataSerie) => {
            let growthPredictionDataSeriesOptions: SeriesOptionsType = {
              id: `${growthPredictionDataSerie.name}_growthPrediction`,
              type: 'line',
              name: `${this.getBarnName(growthPredictionDataSerie.name)} ${this.locale.translate(
                'within-cycle-performance.kpis.GrowthPrediction',
              )}`,
              data: [],
              color: '#007f2d',
              connectNulls: true,
              visible: false,
              showInLegend: false,
              dashStyle: 'Dash',
              tooltip: {
                pointFormatter() {
                  return `${this.y} ${kpi.uom}`;
                },
              },
              events: {
                legendItemClick: this.legendItemClickCallback,
              },
            };

            const correspondingGrowthPredictionDataSerie =
              growthPredictionDataSerie?.series[this.getKpiName(kpi, isCumulative)];
            if (
              correspondingGrowthPredictionDataSerie &&
              correspondingGrowthPredictionDataSerie.length > 0
            ) {
              growthPredictionDataSeriesOptions.visible = true;
              const data = correspondingGrowthPredictionDataSerie[0].values
                .filter((c) => Number.isFinite(c.value))
                .map((c) => [
                  Number(c.dimensionValue) + Number(dayOffset),
                  this.getRoundedNumber(c.value, kpi),
                ]);
              const lastDay = data.length > 0 ? data[data.length - 1][0] : null;
              this.isEmptyLineSeries = lastDay === null;
              growthPredictionDataSeriesOptions = {
                ...growthPredictionDataSeriesOptions,
                data,
                visible: true,
                showInLegend: true,
              };
              showChart = true;
            }

            this.lineChartDataSeries = [
              ...this.lineChartDataSeries.filter(
                (s) => s.id !== `${growthPredictionDataSerie.name}_growthPrediction`,
              ),
              growthPredictionDataSeriesOptions,
            ];
            this.lineTimeSeriesOptions.series = this.lineChartDataSeries;
          });

          // REF.VAL.SHEET GRAPH LINE
          let rvsSeriesOptions: SeriesOptionsType = {
            id: 'rvs',
            type: 'line',
            color: '#c0c0c0',
            connectNulls: true,
            dashStyle: 'ShortDash',
            name: `${this.locale.translate('reference-value-sheet.create.title')}: ${
              this.selectedReferenceValueSheet?.name
            }`,
            data: [],
            showInLegend: false,
            visible: false,
            tooltip: {
              pointFormatter() {
                return `${this.y} ${kpi.uom}`;
              },
            },
            events: {
              legendItemClick: this.legendItemClickCallback,
            },
          };

          if (refValSheetSeries !== undefined && this.selectedReferenceValueSheet) {
            const correspondingRvsSeries =
              refValSheetSeries?.series[this.getKpiName(kpi, isCumulative)];
            if (correspondingRvsSeries && correspondingRvsSeries.length > 0) {
              const data = correspondingRvsSeries[0].values
                .filter((c) => Number.isFinite(c.value))
                .map((c) => [
                  Number(c.dimensionValue) + Number(dayOffset),
                  this.getRoundedNumber(c.value, kpi),
                ]);
              rvsSeriesOptions = {
                ...rvsSeriesOptions,
                data,
                visible: true,
                showInLegend: true,
              };
              if (this.isEmptyLineSeries && data.length > 0) {
                this.isEmptyLineSeries = false;
              }
              showChart = true;
            }
          }

          this.lineChartDataSeries = [
            ...this.lineChartDataSeries.filter((s) => s.id !== 'rvs'),
            rvsSeriesOptions,
          ];
          this.lineTimeSeriesOptions.series = this.lineChartDataSeries;

          this.isLineChartVisible = showChart;
          this.isColumnChartVisible = false;

          this.redrawGraph();
        } else {
          // Separate configuration for the litter quality KPI.
          this.columnTimeSeriesOptions = {
            chart: {
              height: '500px',
              type: 'column',
            },
            title: {
              text: title,
            },
            xAxis: {
              tickInterval: 1,
              title: {
                text: this.locale.translate('wdo-registration.select-week'),
              },
            },
            yAxis: {
              min: 0,
              title: {
                text: '',
              },
            },
            credits: {
              enabled: false,
            },
            tooltip: {
              headerFormat: '<span><b>week: {point.x}</b></span><br>',
              // prettier-ignore

              pointFormatter: function() {
                const pointColor = this.color || this.series.color;
                const barnLabel = getBarnName(<string>this.series.userOptions.stack);
                const percentage = Math.round(this.percentage * 10) / 10;

                return (
                  `<span style="color:${pointColor}">\u25CF</span>` +
                  `<span><b>${barnLabel} ${this.series.name}</b></span>:` +
                  `${percentage}%<br/>`
                );
              },
              shared: true,
            },
            plotOptions: {
              series: {
                stacking: 'percent',
                dataLabels: {
                  enabled: true,
                  align: 'right',
                  verticalAlign: 'top',
                  // prettier-ignore

                  formatter: function() {
                    // get barn label, used in the columns
                    return getBarnName(<string>this.series.userOptions.stack);
                  },
                },
                events: {
                  legendItemClick: this.legendItemClickCallback,
                },
              },
            },
          };

          this.columnChartDataSeries = [];
          wdoSeries?.forEach((barnSeries, index) => {
            const cakedSeries =
              barnSeries?.series[WithinCyclePerformanceKpisEnum.LITTER_QUALITY_CAKED];
            const looseSeries =
              barnSeries?.series[WithinCyclePerformanceKpisEnum.LITTER_QUALITY_LOOSE];
            const wetSeries = barnSeries?.series[WithinCyclePerformanceKpisEnum.LITTER_QUALITY_WET];

            const looseSeriesOptions: SeriesOptionsType = {
              id: 'loose',
              type: 'column',
              name: `${this.locale.translate('within-cycle-performance.kpis.LitterQualityLoose')}`,
              data:
                looseSeries?.length > 0
                  ? looseSeries[0].values
                      .filter((c) => Number.isFinite(c.value))
                      .map((c) => [c.dimensionValue, this.getRoundedNumber(c.value, kpi)])
                  : [],
              color: '#7bb293',
              stack: barnSeries.name,
              visible: true,
              showInLegend: index === 0,
              legendIndex: 0,
            };

            const cakedSeriesOptions: SeriesOptionsType = {
              id: 'caked',
              type: 'column',
              name: `${this.locale.translate('within-cycle-performance.kpis.LitterQualityCaked')}`,
              data:
                cakedSeries?.length > 0
                  ? cakedSeries[0].values
                      .filter((c) => Number.isFinite(c.value))
                      .map((c) => [c.dimensionValue, this.getRoundedNumber(c.value, kpi)])
                  : [],
              color: '#f5eab5',
              stack: barnSeries.name,
              visible: true,
              showInLegend: index === 0,
              legendIndex: 1,
            };

            const wetSeriesOptions: SeriesOptionsType = {
              id: 'wet',
              type: 'column',
              name: `${this.locale.translate('within-cycle-performance.kpis.LitterQualityWet')}`,
              data:
                wetSeries?.length > 0
                  ? wetSeries[0].values
                      .filter((c) => Number.isFinite(c.value))
                      .map((c) => [c.dimensionValue, this.getRoundedNumber(c.value, kpi)])
                  : [],
              color: '#d4953d',
              stack: barnSeries.name,
              visible: true,
              showInLegend: index === 0,
              legendIndex: 2,
            };

            showChart = true;

            this.columnChartDataSeries = [
              ...this.columnChartDataSeries,
              wetSeriesOptions,
              cakedSeriesOptions,
              looseSeriesOptions,
            ];

            this.isEmptyColumnSeries = looseSeriesOptions.data.length === 0;
          });

          this.isColumnChartVisible = showChart;
          this.isLineChartVisible = false;
        }
      },
    ),
  );

  selectedCycle: Cycle = undefined;
  selectedCycleId: number = undefined;
  selectedBarns: Barn[];
  selectedFeedAdvice: FeedAdvice;
  selectedReferenceValueSheet: ReferenceValueSheet;
  dayOldChicks: DayOldChick[];
  barnOptions: Barn[];
  isMobile$ = this.breakpoint.ltSm$;
  cycleGeneralAction$: Observable<SummaryCardAction[]> = this.isMobile$.pipe(
    map((isMobile) => [
      {
        id: 'goto-general-tab',
        label: isMobile ? undefined : this.locale.translate('edit'),
        icon: 'arrow_forward',

        callback: () => {
          this.router.navigate(['/cycle', this.selectedCycle.id]);
        },
      },
    ]),
  );

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.selectedCycle$.pipe(takeUntil(this.destroyed$)).subscribe((selectedCycle) => {
      this.selectedCycle = selectedCycle;
      this.selectedCycleId = selectedCycle.id;
      this.setCycleSpecificInformation();
    });

    this.sortedCycles$.pipe(takeUntil(this.destroyed$)).subscribe((cycles) => {
      this.sortedCycles = cycles;
    });

    this.chartDataSeriesSubscription = this.chartDataSeries$.subscribe();

    this.unitOfMeasureSettings$.pipe().subscribe((settings) => {
      this.unitOfMeasureSettings = settings;
    });

    if (this.brandingService.isTenant('Agravis')) {
      this.customizedLogoSrc = BrandingLogoSrc.AgravisLogo;
      this.showAgrifirmLogo = false;
    }
  }

  getRoundedNumber(num: number, kpi: WithinCyclePerformanceKpi): number {
    if (num === undefined || num === null || Number.isNaN(num) || !Number.isFinite(num)) {
      return Number.NaN;
    } else {
      return +num.toFixed(kpi.numberOfDecimals);
    }
  }

  ngOnDestroy() {
    if (this.chartDataSeriesSubscription) {
      this.chartDataSeriesSubscription.unsubscribe();
    }

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  changeCycleSelection(selection: MatSelectChange) {
    this.dispatchSelectedCycleId(selection.value as number);
  }

  dispatchSelectedCycleId(cycleId: number) {
    this.store.dispatch(cycleActions.setSelectedCycle({ cycleId }));
  }

  changeBarnSelection(_selection: MatSelectChange) {
    this.calculatePanels();
  }

  private setCycleSpecificInformation() {
    combineLatest([this.feedAdvices$, this.referenceValueSheets$])
      .pipe(take(1))
      .subscribe(([feedAdvices, referenceValueSheets]) => {
        this.showCycleStartInFutureWarning = new Date(this.selectedCycle?.cycleDate) > new Date();

        // Barns setup.
        this.barnOptions = [];

        // Check in case of older cycles that cycles use barns which do not exist anymore.
        this.selectedCycle?.dayOldChicks?.forEach((d) => {
          if (!this.barnOptions.find((b) => b.id === d.barn?.id)) {
            this.barnOptions.push(d.barn);
          }
        });

        // pre-select all barns
        this.selectedBarns = this.barnOptions;

        if (this.selectedCycle && this.barnOptions.length > 0) {
          if (this.selectedBarns) {
            const correspondingBarns: Barn[] = this.barnOptions.filter((barnOption) =>
              this.selectedBarns.includes(barnOption),
            );
            if (correspondingBarns) {
              this.selectedBarns = correspondingBarns;
            } else {
              this.selectedBarns = this.barnOptions;
            }
          } else {
            this.selectedBarns = this.barnOptions;
          }
          this.calculatePanels();

          this.selectedFeedAdvice = feedAdvices.find(
            (f) => f.id === this.selectedCycle.feedAdviceId,
          );
          this.selectedReferenceValueSheet = referenceValueSheets.find(
            (f) => f.id === this.selectedCycle.referenceValueSheetId,
          );

          if (this.selectedReferenceValueSheet?.id) {
            this.store.dispatch(
              actions.loadRefValSheetDataSeriesRequest({ id: this.selectedReferenceValueSheet.id }),
            );
          } else {
            this.store.dispatch(
              actions.loadRefValSheetDataSeriesSuccess({ refValSheetDataSeries: undefined }),
            );
          }
        }
      });
  }

  private calculatePanels() {
    if (this.selectedCycle && this.selectedBarns.length > 0) {
      this.store.dispatch(
        actions.loadWdoDataSeriesRequest({
          cycleId: this.selectedCycle.id,
          barnIds: this.selectedBarns.map((b) => b.id),
        }),
      );

      this.store.dispatch(
        actions.loadGrowthPredictionDataSeriesRequest({
          cycleId: this.selectedCycle.id,
          barnIds: this.selectedBarns.map((b) => b.id),
        }),
      );

      this.store.dispatch(
        actions.loadObservationsForCycleRequest({
          cycleId: this.selectedCycle.id,
          unitOfMeasureSettings: this.unitOfMeasureSettings,
        }),
      );

      this.dayOldChicks = this.selectedCycle.dayOldChicks
        .filter((d) => this.selectedBarns.includes(d.barn))
        .sort((a, b) => (new Date(b.arrivalDate) < new Date(a.arrivalDate) ? 1 : -1));
    } else {
      this.store.dispatch(
        actions.loadWdoDataSeriesSuccess({
          wdoDataSeries: undefined,
        }),
      );

      this.store.dispatch(
        actions.loadGrowthPredictionDataSeriesSuccess({
          growthPredictionDataSeries: undefined,
        }),
      );
    }
  }

  private createWdoSeriesOptionsForKpi(
    barnSeries: DataSeries<number, number>,
    kpi: WithinCyclePerformanceKpi,
    isCumulative: boolean,
    dayOffset: number,
  ): SeriesOptionsType[] {
    switch (kpi.kpi) {
      case WithinCyclePerformanceKpisEnum.INDOOR_OUTDOOR_TEMPERATURE: {
        const indoorTemperatureKpi = this.getKpiByEnum(
          WithinCyclePerformanceKpisEnum.INDOOR_TEMPERATURE,
        );
        const outdoorTemperatureKpi = this.getKpiByEnum(
          WithinCyclePerformanceKpisEnum.OUTDOOR_TEMPERATURE,
        );
        return [
          this.createWdoSeriesOptionsForSingleKpi(
            barnSeries,
            indoorTemperatureKpi,
            isCumulative,
            dayOffset,
            true,
          ),
          this.createWdoSeriesOptionsForSingleKpi(
            barnSeries,
            outdoorTemperatureKpi,
            isCumulative,
            dayOffset,
            true,
          ),
        ];
      }

      case WithinCyclePerformanceKpisEnum.INDOOR_OUTDOOR_HUMIDITY: {
        const indoorHumidityKpi = this.getKpiByEnum(WithinCyclePerformanceKpisEnum.INDOOR_HUMIDITY);
        const outdoorHumidityKpi = this.getKpiByEnum(
          WithinCyclePerformanceKpisEnum.OUTDOOR_HUMIDITY,
        );
        return [
          this.createWdoSeriesOptionsForSingleKpi(
            barnSeries,
            indoorHumidityKpi,
            isCumulative,
            dayOffset,
            true,
          ),
          this.createWdoSeriesOptionsForSingleKpi(
            barnSeries,
            outdoorHumidityKpi,
            isCumulative,
            dayOffset,
            true,
          ),
        ];
      }

      default:
        return [
          this.createWdoSeriesOptionsForSingleKpi(barnSeries, kpi, isCumulative, dayOffset, false),
        ];
    }
  }

  private createWdoSeriesOptionsForSingleKpi(
    barnSeries: DataSeries<number, number>,
    kpi: WithinCyclePerformanceKpi,
    isCumulative: boolean,
    dayOffset: number,
    includeKpiNameInLegend: boolean,
  ): SeriesOptionsType {
    let wdoSeriesOptions: SeriesOptionsType = {
      id: barnSeries.name,
      type: 'line',
      name: this.getSeriesLegendLabel(barnSeries.name, kpi, includeKpiNameInLegend),
      data: [],
      connectNulls: true,
      visible: false,
      showInLegend: false,
      tooltip: {
        pointFormatter() {
          return `${this.y} ${kpi.uom}`;
        },
      },
      events: {
        legendItemClick: this.legendItemClickCallback,
      },
    };

    const kpiName = this.getKpiName(kpi, isCumulative);
    const correspondingWdoSeries = barnSeries?.series[kpiName];
    if (correspondingWdoSeries && correspondingWdoSeries.length > 0) {
      wdoSeriesOptions.visible = true;
      const finiteValues = correspondingWdoSeries[0].values.filter((c) => Number.isFinite(c.value));

      const data = this.getSeriesData(finiteValues, Number(dayOffset), kpi);
      const lastDay = data.length > 0 ? data[data.length - 1].x : null;
      this.isEmptyLineSeries = lastDay === null;
      wdoSeriesOptions = {
        ...wdoSeriesOptions,
        data,
        visible: true,
        showInLegend: true,
      };
    }

    return wdoSeriesOptions;
  }

  private getSeriesData(
    seriesItems: DataSerieItem<number, number>[],
    dayOffset: number,
    kpi: WithinCyclePerformanceKpi,
  ): DayPoint[] {
    const data: DayPoint[] = [];

    for (const currentPoint of seriesItems) {
      data.push({
        x: currentPoint.dimensionValue + dayOffset,
        y: this.getRoundedNumber(currentPoint.value, kpi),
        day: currentPoint.dimensionValue + dayOffset,
      } as DayPoint);
    }

    return data;
  }

  private legendItemClickCallback(event: Highcharts.SeriesLegendItemClickEventObject) {
    const clickedSeries = event.target;
    const allOtherSeriesVisible =
      clickedSeries.chart.series.find(
        (s) => s.options.showInLegend === true && s.visible === false && s !== clickedSeries,
      ) === undefined;
    const allOtherSeriesHidden =
      clickedSeries.chart.series.find(
        (s) => s.options.showInLegend === true && s.visible === true && s !== clickedSeries,
      ) === undefined;

    if (clickedSeries.visible) {
      if (allOtherSeriesVisible) {
        // hide all series except the clicked series
        event.preventDefault();
        clickedSeries.chart.series.forEach((series) => {
          if (series !== event.target) {
            series.setVisible(false);
          }
        });
      }

      if (allOtherSeriesHidden) {
        // show all series in the chart
        event.preventDefault();
        clickedSeries.chart.series.forEach((series) => {
          series.setVisible(true);
        });
      }
    }
  }

  private getBarnName(stack: string): string {
    return this.barnOptions?.find((b) => b.id === Number(stack.split('_')[3]))?.name;
  }

  private getKpiName(kpi: WithinCyclePerformanceKpi, isCumulative: boolean): string {
    return kpi.kpi.toString() + (isCumulative ? 'Cumulative' : '');
  }

  private getKpiByEnum(kpiEnum: WithinCyclePerformanceKpisEnum): WithinCyclePerformanceKpi {
    return WithinCyclePerformanceKpis.find((kpi) => kpi.kpi === kpiEnum);
  }

  private getSeriesLegendLabel(
    stack: string,
    kpi: WithinCyclePerformanceKpi,
    includeKpiNameInLegend: boolean,
  ) {
    if (includeKpiNameInLegend) {
      const kpiTitle = this.locale.translate(`within-cycle-performance.kpis.${kpi.kpi.toString()}`);
      return `${this.getBarnName(stack)} (${kpiTitle})`;
    }

    return this.getBarnName(stack);
  }

  private redrawGraph() {
    if (this.lineChartComponent?.chartRef) {
      this.lineChartComponent.chartRef.series.forEach((series) => {
        const dataseries = this.lineChartDataSeries.find((s) => s.id === series.name);
        if (dataseries) {
          series.update(dataseries);
        }
      });
    }
  }

  onKpiChange(kpi: WithinCyclePerformanceKpi) {
    this.selectedKpi$.next(kpi);
    if (!kpi.hasCumulativeOption) {
      this.isCumulativeKpisSelected$.next(false);
    }
  }

  openWeekRegistrations(cycle: Cycle) {
    this.store
      .select(getObservations)
      .pipe(take(1))
      .subscribe((observations) => {
        const weekRegistrations =
          this.cycleService.convertObservationsRegistrationToWeekRegistrations(
            observations,
            this.barnOptions,
          );

        this.dialog
          .fullscreenDialog(
            WDORegistrationComponent,
            {
              selectedBarn: this.selectedBarns[0],
              barns: this.barnOptions,
              weekRegistrations,
              cycle,
              confirmButton: true,
              confirmButtonText: `${this.locale.translate('save')}`,
              pushHistoryState: false,
              isDetailedView: true,
              altButton: true,
              altButtonText: this.locale.translate('wdo-registration.next-day'),
              unitOfMeasureSettings: this.unitOfMeasureSettings,
            },
            {
              maxWidth: '100vw',
            },
          )
          .pipe(take(1))
          .subscribe(() => this.calculatePanels());
      });
  }

  openWeekRegistrationsOverview(cycle: Cycle) {
    this.store
      .select(getObservations)
      .pipe(take(1))
      .subscribe((observations) => {
        const weekRegistrations =
          this.cycleService.convertObservationsRegistrationToWeekRegistrations(
            observations,
            this.barnOptions,
          );

        this.dialog.fullscreenDialog(WDORegistrationOverviewComponent, {
          selectedBarn: this.selectedBarns[0],
          barns: this.barnOptions,
          weekRegistrations,
          cycle,
          pushHistoryState: false,
          unitOfMeasureSettings: this.unitOfMeasureSettings,
        });
      });
  }

  printButtonIsVisible() {
    const today = new Date().valueOf();
    const startDate = new Date(this.selectedCycle?.cycleDate).valueOf();
    const endDate =
      this.selectedCycle?.cycleEndDate !== null
        ? new Date(this.selectedCycle?.cycleEndDate).valueOf()
        : null;

    let diffInDays = 0;
    if (endDate === null) {
      diffInDays = this.daysBetween(startDate, today);
    } else if (today >= endDate) {
      diffInDays = this.daysBetween(startDate, endDate);
    } else if (today < endDate) {
      diffInDays = this.daysBetween(startDate, today);
    }

    return diffInDays > 7;
  }

  csvIndexIsValid(index: number) {
    const cycleStartDate = new Date(this.selectedCycle.cycleDate).setDate(
      new Date(this.selectedCycle.cycleDate).getDate(),
    );

    const cycleEndDate = new Date(this.selectedCycle.cycleEndDate).setDate(
      new Date(this.selectedCycle.cycleEndDate).getDate(),
    );

    const cycleStartDateValidationIsValid =
      new Date(this.selectedCycle.cycleDate).setDate(
        new Date(this.selectedCycle.cycleDate).getDate() + index,
      ) >= cycleStartDate;

    let cycleEndDateValidationIsValid = true;

    if (this.selectedCycle.cycleEndDate) {
      cycleEndDateValidationIsValid =
        new Date(this.selectedCycle.cycleDate).setDate(
          new Date(this.selectedCycle.cycleDate).getDate() + index,
        ) <= cycleEndDate;
    }

    return (
      index <=
        (this.selectedReferenceValueSheet?.referenceValueSheetRows
          ? this.selectedReferenceValueSheet.referenceValueSheetRows.length
          : 42) &&
      cycleEndDateValidationIsValid &&
      cycleStartDateValidationIsValid
    );
  }

  private getIntervalTypeFactor(intervalType: IntervalType) {
    return intervalType === IntervalType.Day ? 1 : 1 * 7 - 1;
  }

  downloadTemplate() {
    this.dialog
      .select({
        options: this.selectOptions,
        selected: this.selectedIntervalTypeOption,
        emptyText: 'No options available',
        pushHistoryState: false,
      })
      .subscribe(({ status, result }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          this.selectedIntervalTypeOption = result;

          let csvContent = 'data:text/csv;charset=utf-8,';
          const columnNamesCSV = [
            `${this.locale.translate('date')} (${this.csvDateFormat})`,
            this.selectedIntervalTypeOption === IntervalType.Day
              ? this.locale.translate('days')
              : this.locale.translate('weeks'),
            this.locale.translate('wdo-labels.mortality'),
            this.locale.translate('wdo-labels.selection'),
            this.locale.translate('wdo-labels.selectionSmall'),
            this.locale.translate('wdo-labels.selectionLegProblems'),
            this.locale.translate('wdo-labels.feedIntake'),
            this.locale.translate('wdo-labels.waterIntake'),
            this.locale.translate('wdo-labels.indoorTemperature'),
            this.locale.translate('wdo-labels.outdoorTemperature'),
            this.locale.translate('wdo-labels.indoorHumidity'),
            this.locale.translate('wdo-labels.outdoorHumidity'),
            this.locale.translate('wdo-labels.weight'),
            this.locale.translate('wdo-labels.litterQualities.Loose'),
            this.locale.translate('wdo-labels.litterQualities.Caked'),
            this.locale.translate('wdo-labels.litterQualities.Wet'),
          ];

          const row = columnNamesCSV.join(this.csvDelimiter);
          csvContent += row + '\r\n';

          const factor = this.getIntervalTypeFactor(this.selectedIntervalTypeOption);
          const indexInitialValue = this.selectedIntervalTypeOption === IntervalType.Day ? 0 : 1;

          for (let index = indexInitialValue; this.csvIndexIsValid(index * factor); index++) {
            const intervalValue = [
              formatDate(
                new Date(this.selectedCycle.cycleDate).setDate(
                  new Date(this.selectedCycle.cycleDate).getDate() + index * factor,
                ),
                this.csvDateFormat,
                'en',
              ),
              index.toString(),
            ];
            const row = intervalValue.join(this.csvDelimiter);
            csvContent += row + '\r\n';
          }

          const encodedUri = encodeURI(csvContent);
          const link = document.createElement('a');
          link.setAttribute('type', 'hidden');
          link.href = encodedUri;
          link.download = `poultrynext_performance-template_${this.selectedIntervalTypeOption}_${
            this.selectedCycle.cycleNumber
          }_${formatDate(new Date(), this.csvDateFormat, 'en').trim()}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      });
  }

  litterQualityIsValid(loose: number, caked: number, wet: number) {
    if (loose === null && caked === null && wet === null) {
      return true;
    }

    if (loose + caked + wet === 100) {
      return true;
    }

    return false;
  }

  parseValue(value: string): number {
    return !isNaN(parseFloat(value?.replace(/,/g, '.')))
      ? parseFloat(value?.replace(/,/g, '.'))
      : null;
  }

  uploadCSV() {
    this.dialog
      .dialog(ImportComponent, {
        selectedCycle: this.selectedCycle,
        intervalTypes: [IntervalType.Day, IntervalType.Week],
        title: this.locale.translate('cycle-overview.weekregistration-upload-csv-title'),
        cancelButtonText: this.locale.translate('cancel'),
        confirmButtonText: this.locale.translate('save'),
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm && result.result?.file) {
          const reader: FileReader = new FileReader();
          reader.onload = (e) => {
            const observations: ObservationsRegistration[] = [];
            const importCycle = this.sortedCycles.find((c) => c.id === result.result?.cycleId);
            let litterQualityIsValid = true;
            let showDateIsInvalidNotification = false;
            const lines: string[] = (e.target.result as string).split('\r\n');

            for (const line of lines) {
              if (line.length > 0) {
                this.csvDelimiter = this.guessDelimiter(e.target.result as string, [',', ';']);
                const lineValues = line.split(this.csvDelimiter);

                if (
                  !this.litterQualityIsValid(
                    this.parseValue(lineValues[13]),
                    this.parseValue(lineValues[14]),
                    this.parseValue(lineValues[15]),
                  )
                ) {
                  this.dialog.notify({
                    title: this.locale.translate(
                      'wdo-registration.validation-errors.total-must-be-100',
                    ),
                    text: this.locale.translate(
                      'wdo-registration.validation-errors.total-must-be-100',
                    ),
                  });
                  litterQualityIsValid = false;
                  break;
                }

                if (
                  !this.dateIsValid(
                    this.parseValue(lineValues[1]),
                    result.result?.intervalType,
                    importCycle,
                  ) &&
                  !this.lineValuesAreEmpty(lineValues)
                ) {
                  showDateIsInvalidNotification = true;
                }

                if (
                  this.parseValue(lineValues[1]) !== null &&
                  this.parseValue(lineValues[1]) >= 0 &&
                  this.dateIsValid(
                    this.parseValue(lineValues[1]),
                    result.result?.intervalType,
                    importCycle,
                  ) &&
                  !this.lineValuesAreEmpty(lineValues)
                ) {
                  observations.push(<ObservationsRegistration>{
                    barnId: result.result?.barnId,
                    cycleId: result.result?.cycleId,
                    intervalValue: this.parseValue(lineValues[1]),
                    intervalType: result.result?.intervalType,
                    mortality: this.parseValue(lineValues[2]),
                    selection: this.parseValue(lineValues[3]),
                    selectionLegProblems: this.parseValue(lineValues[4]),
                    selectionSmall: this.parseValue(lineValues[5]),
                    feedIntakeInKg: this.parseValue(lineValues[6]),
                    waterIntakeInL: this.parseValue(lineValues[7]),
                    indoorTemperatureInCelsius: this.parseValue(lineValues[8]),
                    outdoorTemperatureInCelsius: this.parseValue(lineValues[9]),
                    indoorHumidityInPercentages: this.parseValue(lineValues[10]),
                    outdoorHumidityInPercentages: this.parseValue(lineValues[11]),
                    chickWeightInGrams: this.parseValue(lineValues[12]),
                    litterQualityCakedPercentage: this.parseValue(lineValues[13]),
                    litterQualityLoosePercentage: this.parseValue(lineValues[14]),
                    litterQualityWetPercentage: this.parseValue(lineValues[15]),
                  });
                }
              }
            }
            if (litterQualityIsValid) {
              this.store.dispatch(
                actions.upsertObservationsForCycleRequest({
                  observations,
                  unitOfMeasureSettings: this.unitOfMeasureSettings,
                }),
              );
              this.calculatePanels();

              if (showDateIsInvalidNotification) {
                this.dialog.notify({
                  title: this.locale.translate(
                    'wdo-registration.validation-errors.date-is-not-valid-title',
                  ),
                  text: this.locale.translate(
                    'wdo-registration.validation-errors.date-is-not-valid-description',
                  ),
                });
              }
            }

            this.dispatchSelectedCycleId(importCycle.id);
          };

          reader.readAsText(result.result?.file);
        }
      });
  }

  dateIsValid(intervalValue: number, intervalType: IntervalType, importCycle: Cycle) {
    const factor = this.getIntervalTypeFactor(intervalType);
    let cycleEndDateValidationIsValid = true;

    if (!intervalValue) {
      return cycleEndDateValidationIsValid;
    }

    if (importCycle.cycleEndDate) {
      const cycleEndDate = new Date(importCycle.cycleEndDate).setDate(
        new Date(importCycle.cycleEndDate).getDate(),
      );

      cycleEndDateValidationIsValid =
        new Date(importCycle.cycleDate).setDate(
          new Date(importCycle.cycleDate).getDate() + intervalValue * factor,
        ) <= cycleEndDate;
    } else {
      cycleEndDateValidationIsValid =
        new Date(importCycle.cycleDate).setDate(
          new Date(importCycle.cycleDate).getDate() + intervalValue * factor,
        ) <= new Date().setDate(new Date().getDate());
    }

    return cycleEndDateValidationIsValid;
  }

  private lineValuesAreEmpty(lineValues: string[]) {
    return (
      this.parseValue(lineValues[2]) === null &&
      this.parseValue(lineValues[3]) === null &&
      this.parseValue(lineValues[4]) === null &&
      this.parseValue(lineValues[5]) === null &&
      this.parseValue(lineValues[6]) === null &&
      this.parseValue(lineValues[7]) === null &&
      this.parseValue(lineValues[8]) === null &&
      this.parseValue(lineValues[9]) === null &&
      this.parseValue(lineValues[10]) === null &&
      this.parseValue(lineValues[11]) === null &&
      this.parseValue(lineValues[12]) === null &&
      this.parseValue(lineValues[13]) === null &&
      this.parseValue(lineValues[14]) === null &&
      this.parseValue(lineValues[15]) === null
    );
  }

  private guessDelimiter(text: string, possibleDelimiters: string[]) {
    return possibleDelimiters.find(weedOut);

    function weedOut(delimiter: string) {
      let cache = -1;
      return text.split('\n').every(checkLength);

      function checkLength(line: string) {
        if (!line) {
          return true;
        }

        const length = line.split(delimiter).length;
        if (cache < 0) {
          cache = length;
        }
        return cache === length && length > 1;
      }
    }
  }

  private daysBetween(d1: number, d2: number) {
    return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  }
}
